/**
 * `useEntityChanged(entities, onChange, { paused })` — 서버 `ENTITY_CHANGED` 브로드캐스트에 재조회를 거는 훅.
 *
 * react-query가 아닌 화면(`useEffect` + `useState` 직접 조회) 전용이다. 관심 entity 중 하나라도
 * `stores/entityChanged`의 version이 오르면 `onChange`를 한 번 부른다(같은 렌더 사이에 여러 entity가
 * 함께 올라도 1회 — 한 번의 재조회가 전부를 새로 읽는다).
 *
 * **편집 중인 폼을 덮어쓰지 않는다** — `paused`(그 화면의 dirty 축: 편집 모달 열림·`isDirty`)가 참인 동안
 * 들어온 변경은 **미뤄 두었다가** `paused`가 풀리는 순간 1회 실행한다. 미루는 사이 몇 번이 와도 1회다.
 * 마운트 시점의 version은 「이미 본 것」으로 잡아 첫 렌더에는 부르지 않는다(초기 조회는 화면 자신의 effect).
 *
 * `onChange`는 ref로 잡아 최신 함수를 부른다 — 호출부가 `useCallback`으로 감싸지 않아도 된다
 * (`Organization.tsx`의 `loadOrganization`처럼 매 렌더 새 함수여도 무한 재실행이 없다).
 */
import { useEffect, useRef } from 'react';
import { useEntityChangedStore } from '../stores/entityChanged';
import type { EntityChangedEntity } from '../lib/entityChanged';

interface UseEntityChangedOptions {
  /** 참인 동안은 재조회를 미룬다(편집 중 덮어쓰기 방지). 거짓으로 바뀌면 미룬 변경을 1회 실행. */
  paused?: boolean;
}

export function useEntityChanged(
  entities: readonly EntityChangedEntity[],
  onChange: () => void,
  { paused = false }: UseEntityChangedOptions = {},
): void {
  const versions = useEntityChangedStore((s) => s.versions);
  // 관심 entity의 version만 문자열로 접는다 — 다른 entity의 bump에는 반응하지 않고,
  // `entities` 배열 리터럴의 참조가 매 렌더 바뀌어도 deps가 흔들리지 않는다.
  const stamp = entities.map((e) => `${e}:${versions[e] ?? 0}`).join('|');

  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });
  const seenStampRef = useRef(stamp);
  const pendingRef = useRef(false);

  useEffect(() => {
    if (stamp !== seenStampRef.current) {
      seenStampRef.current = stamp;
      pendingRef.current = true;
    }
    if (pendingRef.current && !paused) {
      pendingRef.current = false;
      onChangeRef.current();
    }
  }, [stamp, paused]);
}
